import React, { useState, useCallback, useMemo } from 'react';
import { Plus, ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useAnimationStore } from '../../store/animationStore';
import { useSceneStore } from '../../store/sceneStore';
import {
  PropertyType,
  PropertyTrack as PropertyTrackType,
  PROPERTY_LABELS,
  PROPERTY_COLORS,
} from '../../../core/types/animation.types';
import { PropertyTrack } from './PropertyTrack';

// Available properties that can be animated
const ANIMATABLE_PROPERTIES: PropertyType[] = [
  'position.x',
  'position.y',
  'rotation',
  'scale.x',
  'scale.y',
  'alpha',
];

interface PropertyTrackListProps {
  animationId: string;
  trackHeaderWidth?: number;
  timelineWidth: number;
  pixelsPerSecond: number;
  scrollLeft: number;
  currentTime: number;
  duration: number;
}

export const PropertyTrackList: React.FC<PropertyTrackListProps> = ({
  animationId,
  trackHeaderWidth = 180,
  timelineWidth,
  pixelsPerSecond,
  scrollLeft,
  currentTime,
  duration,
}) => {
  const { animations, addTrack } = useAnimationStore();
  const { selectedIds, gameObjects } = useSceneStore();

  const [expandedObjects, setExpandedObjects] = useState<Set<string>>(new Set());
  const [showAddPropertyDropdown, setShowAddPropertyDropdown] = useState<string | null>(null);

  const animation = animations[animationId];

  // Group tracks by target object
  const tracksByObject = useMemo(() => {
    if (!animation) return new Map<string, PropertyTrackType[]>();

    const grouped = new Map<string, PropertyTrackType[]>();

    animation.tracks.forEach((track) => {
      const existing = grouped.get(track.targetObjectId) || [];
      grouped.set(track.targetObjectId, [...existing, track]);
    });

    return grouped;
  }, [animation]);

  // Get all unique target object IDs (from tracks + currently selected)
  const allObjectIds = useMemo(() => {
    const ids = new Set<string>();

    // Add objects that already have tracks
    tracksByObject.forEach((_, objectId) => {
      ids.add(objectId);
    });

    // Add currently selected objects
    selectedIds.forEach((id) => {
      if (gameObjects[id]) {
        ids.add(id);
      }
    });

    return Array.from(ids);
  }, [tracksByObject, selectedIds, gameObjects]);

  // Toggle object expansion
  const toggleObjectExpanded = useCallback((objectId: string) => {
    setExpandedObjects((prev) => {
      const next = new Set(prev);
      if (next.has(objectId)) {
        next.delete(objectId);
      } else {
        next.add(objectId);
      }
      return next;
    });
  }, []);

  // Get available properties for adding (ones not already tracked)
  const getAvailableProperties = useCallback(
    (objectId: string): PropertyType[] => {
      const existingTracks = tracksByObject.get(objectId) || [];
      const existingProperties = new Set(existingTracks.map((t) => t.property));

      return ANIMATABLE_PROPERTIES.filter((prop) => !existingProperties.has(prop));
    },
    [tracksByObject]
  );

  // Handle adding a new property track
  const handleAddProperty = useCallback(
    (objectId: string, property: PropertyType) => {
      addTrack(animationId, property, objectId);
      setShowAddPropertyDropdown(null);

      // Ensure the object is expanded to show the new track
      setExpandedObjects((prev) => new Set(prev).add(objectId));
    },
    [animationId, addTrack]
  );

  // Get object name for display
  const getObjectName = useCallback(
    (objectId: string): string => {
      // Special case for demo objects created with new animations
      if (objectId === 'demo-object') {
        return 'Demo Object (Sample)';
      }
      const obj = gameObjects[objectId];
      return obj?.name || `Object (${objectId.slice(0, 6)})`;
    },
    [gameObjects]
  );

  if (!animation) {
    return (
      <div className="flex items-center justify-center h-full text-zinc-500 text-sm">
        No animation selected
      </div>
    );
  }

  if (allObjectIds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-zinc-500 text-sm">
        <p>No objects with animation tracks</p>
        <p className="text-xs">Select a GameObject and add property tracks</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {allObjectIds.map((objectId) => {
        const tracks = tracksByObject.get(objectId) || [];
        const isExpanded = expandedObjects.has(objectId) || tracks.length > 0;
        const objectName = getObjectName(objectId);
        const availableProperties = getAvailableProperties(objectId);
        const isSelected = selectedIds.includes(objectId);

        return (
          <div key={objectId} className="border-b border-zinc-700">
            {/* Object Header */}
            <div
              className={cn(
                'flex items-center gap-2 px-2 py-1.5 cursor-pointer select-none',
                'hover:bg-zinc-800/50 transition-colors',
                isSelected && 'bg-blue-900/20'
              )}
              onClick={() => toggleObjectExpanded(objectId)}
            >
              {/* Expand/Collapse */}
              <button className="p-0.5 text-zinc-400 hover:text-zinc-200">
                {isExpanded ? (
                  <ChevronDown size={14} />
                ) : (
                  <ChevronRight size={14} />
                )}
              </button>

              {/* Object name */}
              <span className="text-sm font-medium text-zinc-200 truncate flex-1">
                {objectName}
              </span>

              {/* Track count badge */}
              {tracks.length > 0 && (
                <span className="text-xs text-zinc-500 px-1.5 py-0.5 bg-zinc-800 rounded">
                  {tracks.length} {tracks.length === 1 ? 'track' : 'tracks'}
                </span>
              )}

              {/* Add Property button */}
              <div className="relative">
                <button
                  className={cn(
                    'p-1 rounded transition-colors',
                    availableProperties.length > 0
                      ? 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
                      : 'text-zinc-600 cursor-not-allowed'
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (availableProperties.length > 0) {
                      setShowAddPropertyDropdown(
                        showAddPropertyDropdown === objectId ? null : objectId
                      );
                    }
                  }}
                  title={
                    availableProperties.length > 0
                      ? 'Add property track'
                      : 'All properties are already tracked'
                  }
                  disabled={availableProperties.length === 0}
                >
                  <Plus size={14} />
                </button>

                {/* Property dropdown */}
                {showAddPropertyDropdown === objectId && (
                  <PropertyDropdown
                    properties={availableProperties}
                    onSelect={(property) => handleAddProperty(objectId, property)}
                    onClose={() => setShowAddPropertyDropdown(null)}
                  />
                )}
              </div>
            </div>

            {/* Property Tracks */}
            {isExpanded && (
              <div className="bg-zinc-900/50">
                {tracks.length === 0 ? (
                  <div className="px-4 py-2 text-xs text-zinc-500 italic">
                    No property tracks. Click + to add.
                  </div>
                ) : (
                  tracks.map((track) => (
                    <PropertyTrack
                      key={track.id}
                      track={track}
                      animationId={animationId}
                      trackHeaderWidth={trackHeaderWidth}
                      timelineWidth={timelineWidth}
                      pixelsPerSecond={pixelsPerSecond}
                      scrollLeft={scrollLeft}
                      currentTime={currentTime}
                      duration={duration}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Property dropdown component
interface PropertyDropdownProps {
  properties: PropertyType[];
  onSelect: (property: PropertyType) => void;
  onClose: () => void;
}

const PropertyDropdown: React.FC<PropertyDropdownProps> = ({
  properties,
  onSelect,
  onClose,
}) => {
  // Close on outside click
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.property-dropdown')) {
        onClose();
      }
    };

    // Add with slight delay to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div
      className={cn(
        'property-dropdown absolute right-0 top-full mt-1 z-50',
        'bg-zinc-800 border border-zinc-600 rounded-lg shadow-xl',
        'min-w-[160px] py-1 overflow-hidden'
      )}
    >
      <div className="px-2 py-1 text-xs text-zinc-500 border-b border-zinc-700">
        Add Property Track
      </div>
      {properties.map((property) => (
        <button
          key={property}
          className={cn(
            'w-full px-3 py-1.5 text-left text-sm text-zinc-200',
            'hover:bg-zinc-700 transition-colors flex items-center gap-2'
          )}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(property);
          }}
        >
          {/* Property color indicator */}
          <div
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: PROPERTY_COLORS[property] }}
          />
          {PROPERTY_LABELS[property]}
        </button>
      ))}
    </div>
  );
};

export default PropertyTrackList;
